import {
  deleteEmptyObjects
} from "./chunk-J7WFOES5.js";
import {
  getEntries
} from "./chunk-IPNO7PON.js";
import {
  panic
} from "./chunk-KKRZEVOT.js";
import {
  DISPLAY_ID_PREFIXES
} from "./chunk-5IRT6RWL.js";
import {
  CountryCode,
  Language,
  MarketCountryCode,
  MeasurementSystem,
  PrivacyLevel
} from "./chunk-VXLCCQ5A.js";

// src/utils/boundingBoxToMultiPolygon.ts
var BOUNDING_BOX_STRING_PATTERN = /^(?:-?\d+(?:\.\d+)?,){3}-?\d+(?:\.\d+)?$/;
function isBoundingBoxDto(value) {
  if (!value || typeof value !== "object") {
    return false;
  }
  const v = value;
  return typeof v.northEast?.lat === "number" && typeof v.northEast?.lng === "number" && typeof v.southWest?.lat === "number" && typeof v.southWest?.lng === "number";
}
function parseBoundingBoxString(boundingBox) {
  if (!BOUNDING_BOX_STRING_PATTERN.test(boundingBox)) {
    return void 0;
  }
  const [northEastLat = "0", northEastLng = "0", southWestLat = "0", southWestLng = "0"] = boundingBox.split(",");
  return {
    northEast: {
      lat: Number.parseFloat(northEastLat),
      lng: Number.parseFloat(northEastLng)
    },
    southWest: {
      lat: Number.parseFloat(southWestLat),
      lng: Number.parseFloat(southWestLng)
    }
  };
}
function convertBoundingBoxDtoToMultiPolygon(bbox) {
  const { northEast, southWest } = bbox;
  const ring = [
    [southWest.lng, southWest.lat],
    [northEast.lng, southWest.lat],
    [northEast.lng, northEast.lat],
    [southWest.lng, northEast.lat],
    [southWest.lng, southWest.lat]
  ];
  return { type: "MultiPolygon", coordinates: [[ring]] };
}
function convertBoundingBoxParamValueToMultiPolygon(value) {
  if (!value) return;
  if (typeof value === "string") {
    const dto = parseBoundingBoxString(value);
    return dto ? convertBoundingBoxDtoToMultiPolygon(dto) : void 0;
  }
  if (isBoundingBoxDto(value)) {
    return convertBoundingBoxDtoToMultiPolygon(value);
  }
  return;
}

// src/utils/mapSearchFilterRequest.ts
import { isEmptyObject } from "es-toolkit";
var mappedFilterPrefixes = [
  "livingSurface",
  "plotSurface",
  "totalSurface",
  "rentAny",
  "rentTotal",
  "salesPrice"
];
var mapSearchFilterRequestToSearchListingsFilters = (filtersRequest) => {
  deleteEmptyObjects(filtersRequest);
  const { propertyKind, sortBy, sortOrder, marketingType, ...filteredRequest } = filtersRequest;
  const filters = {
    ...removeMappedFilterPrefixes(filteredRequest),
    ...getSurfaceFilters(filtersRequest),
    ...getPriceFilterField(filtersRequest)
  };
  return filters;
};
var removeMappedFilterPrefixes = (filtersRequest) => {
  const result = filtersRequest;
  for (const key of Object.keys(filtersRequest)) {
    if (mappedFilterPrefixes.some((prefix) => key.startsWith(prefix))) {
      delete result[key];
    }
  }
  return result;
};
var getPriceFilterFieldFromFilterRequest = (filtersRequest) => {
  const propertyMarketingType = filtersRequest.propertyMarketingType[0];
  const priceFilterField = Object.keys(filtersRequest).find(
    (key) => key.startsWith(propertyMarketingType || "") && !isEmptyObject(filtersRequest[key])
  );
  return priceFilterField ? priceFilterField : void 0;
};
var getPriceFilterField = (filtersRequest) => {
  const priceFilterField = getPriceFilterFieldFromFilterRequest(filtersRequest);
  return priceFilterField ? {
    price: filtersRequest[priceFilterField]
  } : void 0;
};
var getSurfaceFilters = (filtersRequest) => {
  const surfaceFilterKeys = ["livingSurface", "plotSurface", "totalSurface"];
  let surfaceFields = {};
  for (const key of Object.keys(filtersRequest)) {
    const surfaceKey = surfaceFilterKeys.find((prefix) => key.startsWith(prefix));
    if (surfaceKey) {
      surfaceFields = {
        ...surfaceFields,
        [surfaceKey]: filtersRequest[key]
      };
    }
  }
  return surfaceFields;
};
var mapSearchFilterRequestToSearchListingsOptions = (filtersRequest) => {
  const priceFilterField = getPriceFilterFieldFromFilterRequest(filtersRequest);
  const currency = priceFilterField?.slice(-3).toUpperCase();
  const surfaceFilterField = Object.keys(filtersRequest).find((key) => /(?:sqmt|sqft)$/i.test(key));
  const measurementSystem = surfaceFilterField ? surfaceFilterField.endsWith("Sqft") ? MeasurementSystem.imperial : MeasurementSystem.metric : void 0;
  return {
    currency,
    measurementSystem
  };
};

// src/utils/searchResults.ts
var getSearchListingResultId = (result) => {
  return result.listing?.id ?? result.restricted?.id ?? result.userAccountExclusive?.id ?? panic("SearchListingResult has no id");
};
var isSearchListingResultFull = (result) => result.listing != null;
var isSearchListingResultRestricted = (result) => result.restricted != null;
var isSearchListingResultUserAccountExclusive = (result) => result.userAccountExclusive != null;
var mapSearchListingResults = (results) => results.filter((result) => isSearchListingResultFull(result)).map((result) => result.listing);
var isFullListing = (listing) => "isDevelopmentProject" in listing;
var isRestrictedListing = (listing) => "privacyLevel" in listing && listing.privacyLevel === PrivacyLevel.RESTRICTED;
var isUserAccountExclusiveListing = (listing) => "isUserAccountExclusive" in listing && listing.isUserAccountExclusive === true;

// src/utils/strings.ts
import { sha1 } from "js-sha1";
var decodeHTML = (str) => {
  const htmlEntities = {
    "&#34;": '"',
    "&amp;": "&",
    "&#39;": "'",
    "&gt;": ">",
    "&lt;": "<"
  };
  const entityRegex = new RegExp(`(${Object.keys(htmlEntities).join("|")})`, "g");
  return str.replaceAll(entityRegex, (entity) => htmlEntities[entity]);
};
var formatShopPhoneNumber = (num) => num.replaceAll("-", " ");
var createSessionToken = () => {
  return Math.random().toString(36).slice(2);
};
var shortenMasterDataId = (uuid) => {
  return sha1(uuid).slice(0, 8);
};
var shortenMasterDataIds = (uuids) => uuids.map((shopId) => isUUID(shopId) ? shortenMasterDataId(shopId) : shopId);
var isNumeric = (value) => {
  return /^-?\d+$/.test(value);
};
var hasDisplayIdPrefix = (id) => {
  const regex = new RegExp(`^(${DISPLAY_ID_PREFIXES.join("|")})`);
  return regex.test(id.toUpperCase());
};
var CURRENCY_SYMBOL_MAP = {
  EUR: "\u20AC",
  USD: "$",
  CAD: "CA$",
  GBP: "\xA3",
  CHF: "CHF",
  AED: "AED",
  CLF: "UF",
  CLP: "CLP",
  CNY: "CNY",
  COP: "COP",
  CZK: "CZK",
  DKK: "DKK",
  HKD: "HKD",
  HUF: "Ft",
  OMR: "OMR",
  MUR: "MUR",
  QAR: "QAR",
  SEK: "SEK",
  TRY: "TRY",
  ZAR: "ZAR",
  BSD: "BSD",
  BZD: "BZD",
  CRC: "CRC",
  KYD: "KYD",
  MXN: "MXN",
  PAB: "PAB",
  PLN: "PLN"
};
var UNIT_SYMBOL_MAP = {
  [MeasurementSystem.metric]: "m\xB2",
  [MeasurementSystem.imperial]: "sq ft"
};
var SURFACE_UNIT_MAP = {
  [MeasurementSystem.metric]: "sqmt",
  [MeasurementSystem.imperial]: "sqft"
};
var isUUID = (uuid) => {
  return /^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i.test(uuid);
};
function decodeProfileValues(profile) {
  const decodedProfile = {};
  for (const [key, value] of getEntries(profile)) {
    if (value && typeof value === "string") {
      decodedProfile[key] = decodeHTML(value);
    } else {
      decodedProfile[key] = value;
    }
  }
  return decodedProfile;
}

// src/utils/urls.ts
function ensureHttpsPrefix(url) {
  if (!/^https?:\/\//i.test(url)) {
    return "https://" + url;
  }
  return url.replace(/^http:\/\//i, "https://");
}
var YOUTUBE_URL_REGEX = /(?:(?:youtube(?:-nocookie)?\.com)|(?:youtu\.be))\/.*([\w-]{11}).*/;
function isYoutubeUrl(url) {
  return YOUTUBE_URL_REGEX.test(url);
}
function getVideoEmbedUrl(url) {
  const vimeoRegex = /https?:\/\/(?:www\.)?vimeo\.com\/(?:\D*\/)?(?<videoId>\d+)(?:\/(?<hash>[\w-]+))?\/?/;
  const youtubeVideoId = url.match(YOUTUBE_URL_REGEX)?.[1];
  const vimeoMatch = url.match(vimeoRegex);
  if (youtubeVideoId) {
    return `https://www.youtube-nocookie.com/embed/${youtubeVideoId}`;
  }
  if (vimeoMatch?.groups?.videoId) {
    const { videoId, hash } = vimeoMatch.groups;
    const base = `https://player.vimeo.com/video/${videoId}`;
    return hash ? `${base}?h=${encodeURIComponent(hash)}` : base;
  }
  url = ensureHttpsPrefix(url);
  return url;
}
function prependLocaleToPath(pathOrUrl, language, countryCode) {
  const prefix = [countryCode?.toLowerCase(), language].filter(Boolean).join("/");
  const urlMatch = pathOrUrl.match(/^(https?:\/\/[^/]+)(\/.*)?$/);
  if (urlMatch) {
    const [, origin, pathname = ""] = urlMatch;
    if (pathname.startsWith("/") || pathname.startsWith("?") || pathname === "") {
      return `${origin}/${prefix}${pathname || "/"}`;
    }
    return `${origin}/${prefix}/${pathname}`;
  }
  if (pathOrUrl.startsWith("/") || pathOrUrl.startsWith("?")) {
    return `/${prefix}${pathOrUrl}`;
  }
  return `/${prefix}/${pathOrUrl}`;
}
function getExposePath(listing, language, countryCode) {
  return prependLocaleToPath(`/${listing.isDevelopmentProject ? "projects" : "exposes"}/${listing.id}`, language, countryCode);
}
var replaceLocaleInPath = (pathOrUrl, language, countryCode) => {
  const localeRegex = /^\/(?:(?<countryCode>[A-Za-z]{2}|private-office)(?:\/|$))?(?<language>[A-Za-z]{2})(?:\/|$)/;
  const urlMatch = pathOrUrl.match(/^(https?:\/\/[^/]+)(\/.*)?$/);
  if (urlMatch) {
    const [, origin, pathname = "/"] = urlMatch;
    const existingLocaleMatch2 = pathname.match(localeRegex);
    if (existingLocaleMatch2) {
      const oldCountryCode = existingLocaleMatch2.groups?.countryCode;
      const newPathname = pathname.replace(localeRegex, prependLocaleToPath("", language, countryCode ?? oldCountryCode));
      return `${origin}${newPathname}`;
    } else {
      return prependLocaleToPath(pathOrUrl, language, countryCode);
    }
  }
  const existingLocaleMatch = pathOrUrl.match(localeRegex);
  if (existingLocaleMatch) {
    const oldCountryCode = existingLocaleMatch.groups?.countryCode;
    return pathOrUrl.replace(localeRegex, prependLocaleToPath("", language, countryCode ?? oldCountryCode));
  } else {
    return prependLocaleToPath(pathOrUrl, language, countryCode);
  }
};
var COUNTRY_LANGUAGE_RECORD = {
  [MarketCountryCode.AD]: { languages: ["es", "en", "ca", "fr"], defaultLanguage: "en" },
  [MarketCountryCode.AE]: { languages: ["en", "de"], defaultLanguage: "en" },
  [MarketCountryCode.AT]: { languages: ["de", "en"], defaultLanguage: "en" },
  [MarketCountryCode.BE]: { languages: ["nl", "en", "fr"], defaultLanguage: "en" },
  [MarketCountryCode.BS]: { languages: ["en"], defaultLanguage: "en" },
  [MarketCountryCode.BZ]: { languages: ["es", "en"], defaultLanguage: "en" },
  [MarketCountryCode.CA]: { languages: ["en", "fr", "de", "es"], defaultLanguage: "en" },
  [MarketCountryCode.CH]: { languages: ["de", "en", "fr", "it"], defaultLanguage: "en" },
  [MarketCountryCode.CL]: { languages: ["es", "en"], defaultLanguage: "en" },
  [MarketCountryCode.CO]: { languages: ["es", "en"], defaultLanguage: "en" },
  [MarketCountryCode.CR]: { languages: ["es", "en"], defaultLanguage: "en" },
  [MarketCountryCode.CZ]: { languages: ["cs", "en"], defaultLanguage: "en" },
  [MarketCountryCode.DE]: { languages: ["de", "en"], defaultLanguage: "en" },
  [MarketCountryCode.DK]: { languages: ["da", "en"], defaultLanguage: "en" },
  [MarketCountryCode.ES]: { languages: ["es", "ca", "de", "en"], defaultLanguage: "en" },
  [MarketCountryCode.FR]: { languages: ["fr", "en"], defaultLanguage: "en" },
  [MarketCountryCode.GR]: { languages: ["el", "de", "en"], defaultLanguage: "en" },
  [MarketCountryCode.HR]: { languages: ["hr", "en", "de"], defaultLanguage: "en" },
  [MarketCountryCode.HU]: { languages: ["hu", "en"], defaultLanguage: "en" },
  [MarketCountryCode.IE]: { languages: ["en", "de"], defaultLanguage: "en" },
  [MarketCountryCode.IT]: { languages: ["it", "en", "de"], defaultLanguage: "en" },
  [MarketCountryCode.KY]: { languages: ["en"], defaultLanguage: "en" },
  [MarketCountryCode.LI]: { languages: ["de", "en"], defaultLanguage: "en" },
  [MarketCountryCode.LU]: { languages: ["fr", "de", "en"], defaultLanguage: "en" },
  [MarketCountryCode.MF]: { languages: ["fr", "de", "en"], defaultLanguage: "en" },
  [MarketCountryCode.MG]: { languages: ["fr", "de", "en"], defaultLanguage: "en" },
  // this only exists on dev/stage
  [MarketCountryCode.MU]: { languages: ["fr", "de", "en"], defaultLanguage: "en" },
  [MarketCountryCode.MX]: { languages: ["es", "en"], defaultLanguage: "en" },
  [MarketCountryCode.NL]: { languages: ["nl", "en"], defaultLanguage: "en" },
  [MarketCountryCode.PA]: { languages: ["es", "en", "fr"], defaultLanguage: "en" },
  [MarketCountryCode.PT]: { languages: ["pt", "en", "de"], defaultLanguage: "en" },
  [MarketCountryCode.TC]: { languages: ["en"], defaultLanguage: "en" },
  [MarketCountryCode.US]: { languages: ["en", "de", "es", "fr", "it"], defaultLanguage: "en" },
  [MarketCountryCode.UY]: { languages: ["es", "en"], defaultLanguage: "en" },
  [MarketCountryCode.VG]: { languages: ["en"], defaultLanguage: "en" },
  [MarketCountryCode.ZA]: { languages: ["en"], defaultLanguage: "en" },
  [MarketCountryCode.PL]: { languages: ["pl", "en"], defaultLanguage: "en" },
  [MarketCountryCode["private-office"]]: { languages: ["en", "de", "fr", "it", "es"], defaultLanguage: "en" }
};
var COUNTRY_LANGUAGE_MAP = new Map(
  Object.entries(COUNTRY_LANGUAGE_RECORD)
);
var validateLanguageForCountry = (path, countryCode, language) => {
  if (!path) {
    return replaceLocaleInPath("/propertysearch", language, countryCode);
  }
  if (COUNTRY_LANGUAGE_MAP.has(countryCode) && COUNTRY_LANGUAGE_MAP.get(countryCode)?.languages.includes(language)) {
    return path;
  } else if (COUNTRY_LANGUAGE_MAP.has(countryCode)) {
    return replaceLocaleInPath(path, COUNTRY_LANGUAGE_MAP.get(countryCode).defaultLanguage, countryCode);
  } else {
    console.error("Invalid countryCode");
    return replaceLocaleInPath(path, Language.en, MarketCountryCode.DE);
  }
};
var LANGUAGE_ONLY_COUNTRYCODE_LANGUAGE_MAP = {
  [Language.en]: { countryCode: MarketCountryCode.DE, language: Language.en },
  [Language.de]: { countryCode: MarketCountryCode.DE, language: Language.de },
  [Language.fr]: { countryCode: MarketCountryCode.FR, language: Language.fr },
  [Language.it]: { countryCode: MarketCountryCode.IT, language: Language.it },
  [Language.es]: { countryCode: MarketCountryCode.ES, language: Language.es },
  [Language.el]: { countryCode: MarketCountryCode.GR, language: Language.el },
  [Language.da]: { countryCode: MarketCountryCode.DK, language: Language.da },
  [Language.pt]: { countryCode: MarketCountryCode.PT, language: Language.pt },
  [Language.ca]: { countryCode: MarketCountryCode.ES, language: Language.ca },
  [Language.cs]: { countryCode: MarketCountryCode.CZ, language: Language.cs },
  [Language.nl]: { countryCode: MarketCountryCode.NL, language: Language.nl },
  [Language.hr]: { countryCode: MarketCountryCode.HR, language: Language.hr },
  [Language.hu]: { countryCode: MarketCountryCode.HU, language: Language.hu },
  [Language.pl]: { countryCode: MarketCountryCode.PL, language: Language.pl }
};
var getCountryCodeLanguageRedirectObject = (language, countryCode) => {
  return countryCode ? { countryCode, language } : LANGUAGE_ONLY_COUNTRYCODE_LANGUAGE_MAP[language];
};
var COUNTRYCODE_PLACEID_MAP = {
  [MarketCountryCode.AD]: "ChIJlfCemC71pRIRkn_qeNc-yQc",
  [MarketCountryCode.AE]: "ChIJvRKrsd9IXj4RpwoIwFYv0zM",
  [MarketCountryCode.AT]: "ChIJfyqdJZsHbUcRr8Hk3XvUEhA",
  [MarketCountryCode.BE]: "ChIJl5fz7WR9wUcR8g_mObTy60c",
  [MarketCountryCode.BS]: "ChIJPQ9Isjua1ogR5XnHaoO0PhM",
  [MarketCountryCode.BZ]: "ChIJ-foSwT5bXI8RTuMeP02KSvY",
  [MarketCountryCode.CA]: "ChIJ2WrMN9MDDUsRpY9Doiq3aJk",
  [MarketCountryCode.CH]: "ChIJYW1Zb-9kjEcRFXvLDxG1Vlw",
  [MarketCountryCode.CL]: "ChIJL68lBEHFYpYRHbkCERPhBQU",
  [MarketCountryCode.CO]: "ChIJo5QVrjqkFY4RQKPy7wSaDZo",
  [MarketCountryCode.CR]: "ChIJJcmsIWLlko8RK5qBNSX3VGI",
  [MarketCountryCode.CZ]: "ChIJQ4Ld14-UC0cRb1jb03UcZvg",
  [MarketCountryCode.DE]: "ChIJa76xwh5ymkcRW-WRjmtd6HU",
  [MarketCountryCode.DK]: "ChIJ-1-U7rYnS0YRzZLgw9BDh1I",
  [MarketCountryCode.ES]: "ChIJi7xhMnjjQgwR7KNoB5Qs7KY",
  [MarketCountryCode.FR]: "ChIJMVd4MymgVA0R99lHx5Y__Ws",
  [MarketCountryCode.GR]: "ChIJY2xxEcdKWxMRHS2a3HUXOjY",
  [MarketCountryCode.HR]: "ChIJ7ZXdCghBNBMRfxtm4STA86A",
  [MarketCountryCode.HU]: "ChIJw-Q333uDQUcREBAeDCnEAAA",
  [MarketCountryCode.IE]: "ChIJ-ydAXOS6WUgRCPTbzjQSfM8",
  [MarketCountryCode.IT]: "ChIJA9KNRIL-1BIRb15jJFz1LOI",
  [MarketCountryCode.KY]: "ChIJKaq4Lz6GJY8RXnFwh9PERXA",
  [MarketCountryCode.LI]: "ChIJ_S9HHUQxm0cRibFa3Ta16mA",
  [MarketCountryCode.LU]: "ChIJRyEhyrlFlUcR75LTAvZg22Q",
  [MarketCountryCode.MU]: "ChIJyXRE-U1QfCERMdBrEcLZA0I",
  [MarketCountryCode.MG]: "ChIJRYUj6uOk0SERiBN7l8HjRFI",
  [MarketCountryCode.MX]: "ChIJU1NoiDs6BIQREZgJa760ZO0",
  [MarketCountryCode.NL]: "ChIJu-SH28MJxkcRnwq9_851obM",
  [MarketCountryCode.PA]: "ChIJ4yu-yIMVpo8Rz5ulH03g7nk",
  [MarketCountryCode.PT]: "ChIJ1SZCvy0kMgsRQfBOHAlLuCo",
  [MarketCountryCode.TC]: "ChIJdWu7vlVfS4kR3sGNAoDHEX4",
  [MarketCountryCode.US]: "ChIJCzYy5IS16lQRQrfeQ5K5Oxw",
  [MarketCountryCode.UY]: "ChIJCd5f-zoHdZURSlIWYGGWVUo",
  [MarketCountryCode.VG]: "ChIJuaza8mUQBYwRBRzFGx2JHfk",
  [MarketCountryCode.ZA]: "ChIJURLu2YmmNBwRoOikHwxjXeg",
  [MarketCountryCode.MF]: "ChIJ_U5xCKxvDowRp4huTgqDGbk",
  [MarketCountryCode.PL]: "ChIJuwtkpGSZAEcR6lXMScpzdQk"
};
var PLACEID_COUNTRYCODE_MAP = new Map(
  Object.entries(COUNTRYCODE_PLACEID_MAP).map(([countryCode, placeId]) => [
    placeId,
    countryCode
  ])
);
var COUNTRYCODE_BOUNDINGBOX_MAP = {
  [CountryCode.AD]: {
    northEast: {
      lat: 42.6558938,
      lng: 1.786681
    },
    southWest: {
      lat: 42.42871179999999,
      lng: 1.4135436
    }
  },
  [CountryCode.AE]: {
    northEast: {
      lat: 26.0765,
      lng: 56.4395001
    },
    southWest: {
      lat: 22.6315138,
      lng: 51.4576504
    }
  },
  [CountryCode.AT]: {
    northEast: {
      lat: 49.0205305,
      lng: 17.1608018
    },
    southWest: {
      lat: 46.37233579999999,
      lng: 9.5307348
    }
  },
  [CountryCode.AW]: {
    northEast: {
      lat: 12.6306,
      lng: -69.865
    },
    southWest: {
      lat: 12.4113999,
      lng: -70.0641
    }
  },
  [CountryCode.BE]: {
    northEast: {
      lat: 51.5051242,
      lng: 6.4081
    },
    southWest: {
      lat: 49.4969518,
      lng: 2.5240999
    }
  },
  [CountryCode.BS]: {
    northEast: {
      lat: 27.263412,
      lng: -72.70975390000001
    },
    southWest: {
      lat: 20.9082735,
      lng: -80.4775603
    }
  },
  [CountryCode.BZ]: {
    northEast: {
      lat: 18.4959419,
      lng: -87.41269989999999
    },
    southWest: {
      lat: 15.8856189,
      lng: -89.22758789999999
    }
  },
  [CountryCode.CA]: {
    northEast: {
      lat: 62.936823783415264,
      lng: -51.51602978095872
    },
    southWest: {
      lat: 41.67584010519326,
      lng: -140.3559441764478
    }
  },
  [CountryCode.CH]: {
    northEast: {
      lat: 47.8084546,
      lng: 10.4923401
    },
    southWest: {
      lat: 45.81792,
      lng: 5.9558978
    }
  },
  [CountryCode.CL]: {
    northEast: {
      lat: -17.4983649,
      lng: -66.3327
    },
    southWest: {
      lat: -56.6233,
      lng: -110.0281
    }
  },
  [CountryCode.CO]: {
    northEast: {
      lat: 16.0709376,
      lng: -66.8463122
    },
    southWest: {
      lat: -4.227109899999999,
      lng: -81.8317
    }
  },
  [CountryCode.CR]: {
    northEast: {
      lat: 11.219681,
      lng: -82.51830009999999
    },
    southWest: {
      lat: 5.2981183,
      lng: -87.36164079999999
    }
  },
  [CountryCode.CZ]: {
    northEast: {
      lat: 51.0557185,
      lng: 18.8592361
    },
    southWest: {
      lat: 48.5518092,
      lng: 12.090589
    }
  },
  [CountryCode.DE]: {
    northEast: {
      lat: 55.0815,
      lng: 15.0418962
    },
    southWest: {
      lat: 47.270114,
      lng: 5.8663425
    }
  },
  [CountryCode.DK]: {
    northEast: {
      lat: 58.02846,
      lng: 15.2298
    },
    southWest: {
      lat: 54.4317001,
      lng: 7.855200099999999
    }
  },
  [CountryCode.ES]: {
    northEast: {
      lat: 43.8504,
      lng: 4.6362
    },
    southWest: {
      lat: 27.4985,
      lng: -18.2648001
    }
  },
  [CountryCode.FR]: {
    northEast: {
      lat: 51.325,
      lng: 8.233209267754392
    },
    southWest: {
      lat: 42.33458672579894,
      lng: -5.47
    }
  },
  [CountryCode.GR]: {
    northEast: {
      lat: 41.7502505,
      lng: 29.654
    },
    southWest: {
      lat: 34.5428,
      lng: 18.9948678
    }
  },
  [CountryCode.HR]: {
    northEast: {
      lat: 46.5550204,
      lng: 19.4473767
    },
    southWest: {
      lat: 42.3385087,
      lng: 13.3649
    }
  },
  [CountryCode.HU]: {
    northEast: {
      lat: 48.585234,
      lng: 22.8966852
    },
    southWest: {
      lat: 45.7370768,
      lng: 16.1136825
    }
  },
  [CountryCode.IE]: {
    northEast: {
      lat: 55.4602,
      lng: -5.668900100000001
    },
    southWest: {
      lat: 51.3066,
      lng: -10.7666001
    }
  },
  [CountryCode.IT]: {
    northEast: {
      lat: 47.092,
      lng: 18.7975999
    },
    southWest: {
      lat: 35.4897,
      lng: 6.6267201
    }
  },
  [CountryCode.KY]: {
    northEast: {
      lat: 19.7616,
      lng: -79.7191
    },
    southWest: {
      lat: 19.2538999,
      lng: -81.42940010000001
    }
  },
  [CountryCode.LI]: {
    northEast: {
      lat: 47.27057430000001,
      lng: 9.6356922
    },
    southWest: {
      lat: 47.04842800000001,
      lng: 9.471682999999999
    }
  },
  [CountryCode.LU]: {
    northEast: {
      lat: 50.182787,
      lng: 6.5312003
    },
    southWest: {
      lat: 49.447879,
      lng: 5.7357019
    }
  },
  [CountryCode.MF]: {
    northEast: {
      lat: 18.1902778,
      lng: -62.7644063
    },
    southWest: {
      lat: 17.8963535,
      lng: -63.3605643
    }
  },
  [CountryCode.MG]: {
    northEast: {
      lat: -11.4369999,
      lng: 50.9985001
    },
    southWest: {
      lat: -26.2146,
      lng: 42.7368
    }
  },
  [CountryCode.MU]: {
    northEast: {
      lat: -19.958717533457538,
      lng: 57.8793651071548
    },
    southWest: {
      lat: -20.550155589083104,
      lng: 57.27386000861176
    }
  },
  [CountryCode.MX]: {
    northEast: {
      lat: 32.7187538,
      lng: -86.5887
    },
    southWest: {
      lat: 14.3895,
      lng: -118.6523001
    }
  },
  [CountryCode.NL]: {
    northEast: {
      lat: 53.6316,
      lng: 7.227510199999999
    },
    southWest: {
      lat: 50.75035260000001,
      lng: 3.3316001
    }
  },
  [CountryCode.PA]: {
    northEast: {
      lat: 9.7145001,
      lng: -77.1584879
    },
    southWest: {
      lat: 7.0409,
      lng: -83.05258130000001
    }
  },
  [CountryCode.PL]: {
    northEast: {
      lat: 54.9054761,
      lng: 24.1458743
    },
    southWest: {
      lat: 49.00204600000001,
      lng: 14.1228641
    }
  },
  [CountryCode.PT]: {
    northEast: {
      lat: 42.156,
      lng: -6.187
    },
    southWest: {
      lat: 36.711,
      lng: -9.837
    }
  },
  [CountryCode.SM]: {
    northEast: {
      lat: 43.993154845866144,
      lng: 12.518217650040356
    },
    southWest: {
      lat: 43.89365049674052,
      lng: 12.402634517547426
    }
  },
  [CountryCode.TC]: {
    northEast: {
      lat: 22.0016285,
      lng: -71.05949989999999
    },
    southWest: {
      lat: 21.1459922,
      lng: -72.52069999999999
    }
  },
  [CountryCode.US]: {
    northEast: {
      lat: 49.42,
      lng: -66.85
    },
    southWest: {
      lat: 24.21,
      lng: -125.12
    }
  },
  [CountryCode.UY]: {
    northEast: {
      lat: -30.0852149,
      lng: -53.0779284
    },
    southWest: {
      lat: -35.1558001,
      lng: -58.4913609
    }
  },
  [CountryCode.VG]: {
    northEast: {
      lat: 18.4239,
      lng: -64.4391
    },
    southWest: {
      lat: 17.5482999,
      lng: -65.1101
    }
  },
  [CountryCode.ZA]: {
    northEast: {
      lat: -22.1254239,
      lng: 38.2216904
    },
    southWest: {
      lat: -47.1313489,
      lng: 16.2816999
    }
  }
};
var DEFAULT_IMAGE = "https://uploadcare.engelvoelkers.com/4a6e9b16-f2a8-4afc-ba26-a7d0cbcb1dbb/";
function getUploadCareImageUrl({ imageId, useWaterMark, isBlurred }) {
  if (!imageId) return DEFAULT_IMAGE;
  const IMAGE_WATERMARK_ID = "131ecb57-f36f-4015-aee3-5851eb9bfa9d";
  return `https://uploadcare.engelvoelkers.com/${imageId}/${useWaterMark ? `-/overlay/${IMAGE_WATERMARK_ID}/60px60p/50p,50p/60p/` : ""}${isBlurred ? `-/blur/200/` : ""}`;
}

export {
  convertBoundingBoxDtoToMultiPolygon,
  convertBoundingBoxParamValueToMultiPolygon,
  mapSearchFilterRequestToSearchListingsFilters,
  mapSearchFilterRequestToSearchListingsOptions,
  getSearchListingResultId,
  isSearchListingResultFull,
  isSearchListingResultRestricted,
  isSearchListingResultUserAccountExclusive,
  mapSearchListingResults,
  isFullListing,
  isRestrictedListing,
  isUserAccountExclusiveListing,
  decodeHTML,
  formatShopPhoneNumber,
  createSessionToken,
  shortenMasterDataId,
  shortenMasterDataIds,
  isNumeric,
  hasDisplayIdPrefix,
  CURRENCY_SYMBOL_MAP,
  UNIT_SYMBOL_MAP,
  SURFACE_UNIT_MAP,
  isUUID,
  decodeProfileValues,
  ensureHttpsPrefix,
  isYoutubeUrl,
  getVideoEmbedUrl,
  prependLocaleToPath,
  getExposePath,
  replaceLocaleInPath,
  COUNTRY_LANGUAGE_RECORD,
  COUNTRY_LANGUAGE_MAP,
  validateLanguageForCountry,
  LANGUAGE_ONLY_COUNTRYCODE_LANGUAGE_MAP,
  getCountryCodeLanguageRedirectObject,
  COUNTRYCODE_PLACEID_MAP,
  PLACEID_COUNTRYCODE_MAP,
  COUNTRYCODE_BOUNDINGBOX_MAP,
  DEFAULT_IMAGE,
  getUploadCareImageUrl
};
